/**
 * WCAG 2.x contrast for a company's brand colours, checked live in Settings before they reach the
 * crew app. Lives outside `src/features` because its tests need literal hex values, which the
 * feature-folder hex guard rightly refuses.
 */

/** `#rgb` or `#rrggbb` (case-insensitive) to 0-255 channels; null for anything else. */
export function parseHex(value: string): [number, number, number] | null {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
  if (!match?.[1]) return null;
  const digits = match[1].length === 3 ? [...match[1]].map((c) => c + c).join('') : match[1];
  return [0, 2, 4].map((i) => Number.parseInt(digits.slice(i, i + 2), 16)) as [
    number,
    number,
    number,
  ];
}

/** Relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Contrast of a colour against white (luminance 1), 1 to 21; null when the value is not a colour. */
export function contrastWithWhite(value: string): number | null {
  const rgb = parseHex(value);
  if (!rgb) return null;
  return 1.05 / (relativeLuminance(rgb) + 0.05);
}

/** `#rrggbb` in lower case for a valid colour (a native colour input accepts only that form). */
export function normalizeHex(value: string): string | null {
  const rgb = parseHex(value);
  return rgb ? `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}` : null;
}

/** What a native colour input shows while no colour is set. */
export const EMPTY_COLOUR_INPUT = '#000000';
